
import React, { useState } from 'react';
import { HelpCircle, X } from 'lucide-react';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogDescription,
  DialogClose
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

/**
 * FloatingHelpButton Component
 * 
 * A floating help button that displays a help dialog when clicked.
 * The button stays fixed at the bottom right corner of the screen.
 */
const FloatingHelpButton = () => {
  // State to control the dialog open/close
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <>
      {/* Floating button fixed to bottom right */}
      <Button
        className="fixed bottom-6 right-6 md:bottom-8 md:right-8 z-50 rounded-full w-12 h-12 p-0 shadow-lg hover:shadow-xl transition-all duration-300 animate-fade-in"
        onClick={() => setIsOpen(true)}
        variant="default"
        size="icon"
        aria-label="Get help"
      >
        <HelpCircle className="h-6 w-6" />
      </Button>
      
      {/* Help Dialog */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <HelpCircle className="h-5 w-5 text-primary" />
              How can we help you?
            </DialogTitle>
            <DialogDescription>
              Get answers to frequently asked questions and learn how to use the platform effectively.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Help sections - each in a card-like container */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-medium mb-2">Getting Started</h3>
              <p className="text-sm text-gray-600">
                Explore our platform by navigating through the sidebar menu. Start with learning modules to build your financial knowledge.
              </p>
            </div>
            
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-medium mb-2">Learning Path</h3>
              <p className="text-sm text-gray-600">
                Complete lessons in order to earn experience points and track your progress. Knowledge checks will test your understanding.
              </p>
            </div>
            
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-medium mb-2">Games & Practice</h3>
              <p className="text-sm text-gray-600">
                Apply what you've learned through interactive financial games and simulations to reinforce concepts.
              </p>
            </div>
          </div>
          
          <div className="mt-4 flex justify-end">
            <DialogClose asChild>
              <Button>Close</Button>
            </DialogClose>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default FloatingHelpButton;
